/* Phase 2G — Video Prompts panel.
 *
 * Renders the persisted video prompt pack produced by
 * `backend/video_prompt_intelligence.py`. This is *prompt intelligence
 * only* — there is NO upload, NO OAuth, NO posting, NO video
 * generation. The UI hands the operator agency-grade prompt packs +
 * production-handoff guidance, and gets out of the way.
 *
 * Backend contract:
 *   GET    /api/projects/{id}/video-prompts
 *   POST   /api/projects/{id}/video-prompts/generate
 *   POST   /api/projects/{id}/video-prompts/regenerate-shot
 *   PATCH  /api/projects/{id}/video-prompts/provider
 *   GET    /api/exports/{id}/video-prompts.pdf
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Film, RefreshCw, Sparkles, AlertCircle, Download, Copy, Camera,
    Layers, Mic, Image as ImageIcon, ShieldAlert, Settings2,
} from "lucide-react";
import { downloadExport } from "@/lib/exports";

const PROVIDER_OPTIONS = [
    { value: "universal",   label: "Universal" },
    { value: "google_flow", label: "Google Flow" },
    { value: "veo",         label: "Veo (Google)" },
    { value: "runway",      label: "Runway" },
    { value: "pika",        label: "Pika" },
    { value: "kling",       label: "Kling" },
    { value: "sora-2",      label: "Sora 2 (OpenAI · Universal Key)" },
];

function copyToClipboard(text, hint = "Copied") {
    try {
        navigator.clipboard.writeText(text || "");
        toast.success(hint);
    } catch (e) {
        toast.error("Copy failed");
    }
}

export default function VideoPromptsPanel({ projectId }) {
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [provider, setProvider] = useState("universal");
    const [activeShotId, setActiveShotId] = useState(null);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const { data } = await api.get(
                `/projects/${projectId}/video-prompts`
            );
            setDoc(data.pack || null);
            if (data.pack?.selected_provider) {
                setProvider(data.pack.selected_provider);
            }
        } catch (e) {
            toast.error(e.userMessage || "Could not load video prompts");
        } finally { setLoading(false); }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    const generate = async () => {
        if (!projectId) return;
        setGenerating(true);
        try {
            const { data } = await api.post(
                `/projects/${projectId}/video-prompts/generate`,
                { selected_provider: provider }
            );
            setDoc(data.pack || null);
            toast.success(
                data.pack?.assumption_warnings?.length
                    ? `Generated with ${data.pack.assumption_warnings.length} warning(s).`
                    : "Video prompt pack generated."
            );
        } catch (e) {
            toast.error(e.userMessage || "Could not generate prompt pack");
        } finally { setGenerating(false); }
    };

    const changeProvider = async (next) => {
        setProvider(next);
        if (!doc) return;
        try {
            await api.patch(
                `/projects/${projectId}/video-prompts/provider`,
                { selected_provider: next }
            );
            // Optimistic: regen so per-shot prompts reflect new provider.
            await generate();
        } catch (e) {
            toast.error(e.userMessage || "Could not switch provider");
        }
    };

    const regenerateShot = async (shotId) => {
        try {
            setActiveShotId(shotId);
            await api.post(
                `/projects/${projectId}/video-prompts/regenerate-shot`,
                { shot_id: shotId, selected_provider: provider }
            );
            await load();
            toast.success(`Shot ${shotId} regenerated.`);
        } catch (e) {
            toast.error(e.userMessage || "Could not regenerate shot");
        } finally { setActiveShotId(null); }
    };

    const downloadPdf = async () => {
        // Phase Final-QA — use shared exports helper so auth uses
        // aivos_token (not the wrong "token" key) and we share the
        // app-wide error/progress UX.
        // NOTE: API base already includes `/api`, so the path here
        // MUST start at `/exports/...` (not `/api/exports/...`) to
        // avoid producing a doubled `/api/api/...` 404.
        await downloadExport(
            `/exports/${projectId}/video-prompts.pdf`,
            `video-prompts-${projectId}.pdf`,
            (err) => toast.error(err?.message || "PDF download failed"),
        );
    };

    // Sprint FGI-lite — per-provider PDF download (reuses same route
    // with the new ?provider= query param).
    const downloadProviderPdf = async (providerKey) => {
        await downloadExport(
            `/exports/${projectId}/video-prompts.pdf?provider=${encodeURIComponent(providerKey)}`,
            `video-prompts-${projectId}-${providerKey}.pdf`,
            (err) => toast.error(err?.message || "PDF download failed"),
        );
    };

    // Sprint FGI-lite — load provider capability matrix once per render.
    const [capabilities, setCapabilities] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(
                    `/projects/${projectId}/video-visual-contract?provider=sora-2`,
                );
                if (!cancelled) setCapabilities(data?.capability_matrix || []);
            } catch (e) { /* best-effort */ }
        })();
        return () => { cancelled = true; };
    }, [projectId]);

    return (
        <div className="card-base p-5" data-testid="video-prompts-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-3 min-w-0">
                    <Film className="w-4 h-4 text-zinc-500" />
                    <h3 className="font-display text-lg">Video prompt pack</h3>
                    {doc?.updated_at && (
                        <span className="text-xs text-zinc-500" data-testid="vp-updated-at">
                            {new Date(doc.updated_at).toLocaleString()}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <select
                        value={provider}
                        onChange={(e) => changeProvider(e.target.value)}
                        className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1 text-xs"
                        data-testid="vp-provider-select"
                    >
                        {PROVIDER_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    {doc && (
                        <button
                            type="button"
                            onClick={downloadPdf}
                            data-testid="vp-download-pdf-btn"
                            className="btn-secondary text-xs"
                        >
                            <Download className="w-3.5 h-3.5" />
                            PDF
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={generate}
                        disabled={generating}
                        data-testid="vp-generate-btn"
                        className="btn-secondary text-xs disabled:opacity-50"
                    >
                        {generating
                            ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            : <Sparkles className="w-3.5 h-3.5" />}
                        {doc ? "Regenerate" : "Generate"}
                    </button>
                </div>
            </div>

            <HonestyBanner doc={doc} />

            {capabilities && capabilities.length > 0 && (
                <details
                    className="rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2 text-[11px] mt-2"
                    data-testid="vp-capability-matrix"
                >
                    <summary className="cursor-pointer text-zinc-300 hover:text-zinc-100 font-semibold">
                        Provider capability matrix ({capabilities.length})
                    </summary>
                    <div className="mt-2 overflow-x-auto">
                        <table className="w-full text-[10.5px]">
                            <thead className="text-zinc-500">
                                <tr>
                                    <th className="text-left py-1 pr-2">Provider</th>
                                    <th className="text-left py-1 pr-2">Status</th>
                                    <th className="text-left py-1 pr-2">T→V</th>
                                    <th className="text-left py-1 pr-2">I→V</th>
                                    <th className="text-left py-1 pr-2">Refs</th>
                                    <th className="text-left py-1 pr-2">Durations</th>
                                    <th className="text-left py-1 pr-2">Sizes</th>
                                    <th className="text-left py-1 pr-2">BYOK</th>
                                    <th className="text-left py-1 pr-2">Export</th>
                                </tr>
                            </thead>
                            <tbody>
                                {capabilities.map((c) => (
                                    <tr key={c.provider} className="border-t border-zinc-800/60">
                                        <td className="py-1 pr-2 text-zinc-200">{c.label}</td>
                                        <td className="py-1 pr-2">
                                            <span className={c.integration_status === "wired"
                                                ? "text-emerald-300"
                                                : "text-amber-300"}>
                                                {c.integration_status}
                                            </span>
                                        </td>
                                        <td className="py-1 pr-2">{c.supports_text_to_video ? "✓" : "—"}</td>
                                        <td className="py-1 pr-2">{c.supports_image_to_video ? "✓" : "—"}</td>
                                        <td className="py-1 pr-2">{c.supports_reference_images ? "✓" : "—"}</td>
                                        <td className="py-1 pr-2 text-zinc-400">
                                            {(c.supported_durations || []).join("/")}s
                                        </td>
                                        <td className="py-1 pr-2 text-zinc-400">
                                            {(c.supported_sizes || []).slice(0, 2).join(", ")}
                                            {(c.supported_sizes || []).length > 2 ? "…" : ""}
                                        </td>
                                        <td className="py-1 pr-2 text-zinc-400">
                                            {c.requires_byok ? "yes" : "—"}
                                        </td>
                                        <td className="py-1 pr-2">
                                            <button
                                                onClick={() => downloadProviderPdf(c.provider)}
                                                className="inline-flex items-center gap-1 text-violet-300 hover:text-violet-200"
                                                data-testid={`vp-download-pdf-${c.provider}`}
                                            >
                                                <Download className="w-3 h-3" />
                                                pdf
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            )}

            {loading && (
                <div className="text-xs text-zinc-500" data-testid="vp-loading">Loading…</div>
            )}

            {!loading && !doc && (
                <div
                    className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-6 text-center"
                    data-testid="vp-empty"
                >
                    No video prompt pack yet.
                    <div className="text-xs mt-2 text-zinc-600">
                        Approve a script + storyboard first, then click{" "}
                        <span className="text-zinc-300">Generate</span> to build a
                        provider-ready prompt pack.
                    </div>
                </div>
            )}

            {!loading && doc && (
                <Pack
                    doc={doc}
                    provider={provider}
                    onRegenerateShot={regenerateShot}
                    activeShotId={activeShotId}
                />
            )}
        </div>
    );
}

function HonestyBanner({ doc }) {
    const warnings = doc?.assumption_warnings || [];
    return (
        <div className="space-y-2 mb-5" data-testid="vp-honesty-banner">
            <div className="text-xs text-zinc-500 leading-relaxed border-l-2 border-violet-500/30 pl-3 bg-violet-500/[0.03] py-2 rounded-r-sm">
                <span className="text-violet-300 font-medium">Production handoff aid.</span>{" "}
                {doc?.honesty_notice ||
                    "Furmais does not generate, upload, schedule, or publish video. " +
                    "Verify each prompt against your selected provider's exact " +
                    "aspect-ratio, duration, reference-image, and dialogue/audio " +
                    "rules before launching a batch."}
                <div className="mt-1.5 text-amber-300">
                    Furmais does not upload assets automatically. These are
                    manual provider upload instructions — copy each prompt and
                    drop your own reference images on the provider&apos;s page.
                </div>
            </div>
            {warnings.length > 0 && (
                <div
                    className="text-xs text-amber-200 leading-relaxed border-l-2 border-amber-500/40 pl-3 bg-amber-500/[0.04] py-2 rounded-r-sm"
                    data-testid="vp-warnings-banner"
                >
                    <span className="font-medium">Checklist warnings</span>
                    <ul className="mt-1 list-disc ml-4 space-y-0.5">
                        {warnings.slice(0, 6).map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}

function Pack({ doc, provider, onRegenerateShot, activeShotId }) {
    const shots = doc.shot_prompts || [];
    const providerBlocks = (doc.provider_prompts || {})[provider] || [];
    const blockByShot = {};
    providerBlocks.forEach(b => { blockByShot[b.shot_id] = b; });

    return (
        <div className="space-y-7" data-testid="vp-pack">
            <SummaryRow doc={doc} provider={provider} />
            <StyleLockCard styleLock={doc.style_lock || {}} />
            <AssetChecklist summary={doc.asset_summary || {}} />
            <section data-testid="vp-shots">
                <SectionHeader icon={Camera} title="Shot prompt cards" />
                <div className="space-y-3">
                    {shots.map((shot) => (
                        <ShotCard
                            key={shot.shot_id}
                            shot={shot}
                            providerBlock={blockByShot[shot.shot_id]}
                            onRegenerate={() => onRegenerateShot(shot.shot_id)}
                            isRegenerating={activeShotId === shot.shot_id}
                        />
                    ))}
                </div>
            </section>
            <ContinuityBible bible={doc.continuity_bible || {}} />
            <QualityDashboard pack={doc} />
        </div>
    );
}

function SectionHeader({ icon: Icon, title }) {
    return (
        <div className="flex items-center gap-2 mb-2">
            <Icon className="w-4 h-4 text-zinc-500" />
            <h3 className="font-display text-base">{title}</h3>
        </div>
    );
}

function SummaryRow({ doc, provider }) {
    return (
        <div
            className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono uppercase tracking-wider"
            data-testid="vp-summary-row"
        >
            <Kv label="Selected provider" value={provider} highlight />
            <Kv label="Recommended" value={doc.recommended_provider} />
            <Kv label="Shots" value={doc.shot_count ?? (doc.shot_prompts || []).length} />
            <Kv label="Pack avg" value={(doc.pack_quality && doc.pack_quality.average) || "—"} />
        </div>
    );
}

function Kv({ label, value, highlight }) {
    return (
        <div className={`border ${highlight ? "border-violet-500/30" : "border-zinc-800"} rounded-sm px-2 py-1.5`}>
            <div className="text-zinc-500">{label}</div>
            <div className={`mt-0.5 ${highlight ? "text-violet-300" : "text-zinc-200"}`}>
                {String(value)}
            </div>
        </div>
    );
}

function StyleLockCard({ styleLock }) {
    const keys = [
        "realism_level", "art_style", "camera_language", "lighting",
        "color_palette", "mood", "pacing", "editing_rhythm",
        "location_environment", "costume_direction", "brand_treatment",
        "dialogue_language_tone", "mixing_rule",
    ];
    return (
        <section data-testid="vp-style-lock">
            <SectionHeader icon={Settings2} title="Active style lock" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-xs">
                {keys.map((k) => (
                    <div key={k} className="flex justify-between border-b border-zinc-900 py-1 gap-3">
                        <span className="text-zinc-500">{k.replace(/_/g, " ")}</span>
                        <span className="text-zinc-300 text-right truncate">
                            {styleLock[k] || "—"}
                        </span>
                    </div>
                ))}
            </div>
        </section>
    );
}

function AssetChecklist({ summary }) {
    const have = summary.have || {};
    const missing = summary.missing_recommended || [];
    return (
        <section data-testid="vp-asset-checklist">
            <SectionHeader icon={ImageIcon} title="Asset & reference checklist" />
            <div className="flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-wider mb-2">
                {Object.entries(have).map(([k, v]) => (
                    <span
                        key={k}
                        className="px-1.5 py-0.5 rounded-sm border border-emerald-500/30 text-emerald-300 bg-emerald-500/5"
                    >
                        {k}: {v}
                    </span>
                ))}
                {Object.keys(have).length === 0 && (
                    <span className="text-zinc-500 normal-case font-sans">
                        No assets uploaded yet.
                    </span>
                )}
            </div>
            {missing.length > 0 && (
                <div className="text-xs text-amber-300 flex flex-wrap gap-1.5 items-center">
                    <ShieldAlert className="w-3 h-3" />
                    Missing recommended:{" "}
                    <span className="font-mono uppercase tracking-wider text-amber-200">
                        {missing.slice(0, 10).join(" · ")}
                    </span>
                </div>
            )}
        </section>
    );
}

function ShotCard({ shot, providerBlock, onRegenerate, isRegenerating }) {
    const block = providerBlock || {};
    return (
        <div
            className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-3"
            data-testid={`vp-shot-${shot.shot_id}`}
        >
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-violet-500/30 text-violet-300 bg-violet-500/5">
                        Shot {shot.shot_number}
                    </span>
                    <span className="text-sm text-zinc-100 font-medium truncate">
                        {shot.scene_title || shot.shot_id}
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Pill label={`dur: ${shot.duration_hint}`} />
                    <Pill label={`asp: ${shot.aspect_ratio}`} />
                    {shot.shot_type && <Pill label={shot.shot_type} />}
                    <button
                        type="button"
                        onClick={onRegenerate}
                        disabled={isRegenerating}
                        className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-300 hover:text-zinc-100 disabled:opacity-50"
                        data-testid={`vp-shot-regen-${shot.shot_id}`}
                    >
                        {isRegenerating ? "…" : "Regenerate"}
                    </button>
                </div>
            </div>

            <p className="text-xs text-zinc-300 leading-relaxed mb-2">
                {shot.description}
            </p>

            <Block label="Ideal prompt" body={block.ideal_prompt || shot.ideal_prompt}
                   testId={`vp-shot-ideal-${shot.shot_id}`} copyHint="Ideal prompt copied" />
            <Block label="Negative prompt" body={block.negative_prompt || shot.negative_prompt}
                   testId={`vp-shot-neg-${shot.shot_id}`} copyHint="Negative prompt copied" muted />
            <Block label="Reference"
                   body={block.reference_upload_instructions || shot.reference_instructions}
                   muted />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-[11px] text-zinc-400">
                {block.first_frame_instruction && (
                    <div className="border-l-2 border-emerald-500/30 pl-2 py-0.5">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-300">First frame:</span>{" "}
                        {block.first_frame_instruction}
                    </div>
                )}
                {block.last_frame_instruction && (
                    <div className="border-l-2 border-emerald-500/30 pl-2 py-0.5">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-300">Last frame:</span>{" "}
                        {block.last_frame_instruction}
                    </div>
                )}
            </div>

            <DialogueRow d={shot.dialogue || {}} />
            <ScoresRow scores={shot.scores || {}} />

            <div className="mt-2 text-[10px] text-zinc-600 flex items-center gap-1.5">
                <AlertCircle className="w-2.5 h-2.5" />
                {block.limitations_warning || "Verify against the provider before launching."}
            </div>
        </div>
    );
}

function Pill({ label }) {
    return (
        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-300 bg-zinc-900/40">
            {label}
        </span>
    );
}

function Block({ label, body, testId, copyHint, muted }) {
    if (!body) return null;
    return (
        <div className={`text-xs mt-1.5 ${muted ? "text-zinc-400" : "text-zinc-200"}`}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                    {label}
                </span>
                <button
                    type="button"
                    onClick={() => copyToClipboard(body, copyHint)}
                    className="text-zinc-500 hover:text-zinc-200"
                    data-testid={testId ? `${testId}-copy` : undefined}
                >
                    <Copy className="w-3 h-3" />
                </button>
            </div>
            <p className="leading-relaxed" data-testid={testId}>{body}</p>
        </div>
    );
}

function DialogueRow({ d }) {
    const keys = [
        ["dialogue_line", "Dialogue"],
        ["voiceover_line", "VO"],
        ["on_screen_text", "On-screen"],
        ["subtitle_text", "Subtitle"],
        ["background_music_direction", "Music"],
        ["sound_design_sfx", "SFX"],
        ["speaker_identity", "Speaker"],
        ["language_tone", "Tone"],
        ["dialogue_timing_within_shot", "Timing"],
    ];
    const hasAny = keys.some(([k]) => (d[k] || "").toString().trim());
    if (!hasAny) return null;
    return (
        <div className="mt-2 border border-zinc-800 bg-zinc-900/40 rounded-sm p-2" data-testid="vp-dialogue">
            <div className="flex items-center gap-2 mb-1">
                <Mic className="w-3 h-3 text-amber-300" />
                <span className="text-[10px] font-mono uppercase tracking-wider text-amber-300">
                    Audio direction
                </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-zinc-300">
                {keys.map(([k, label]) =>
                    d[k] ? (
                        <div key={k}>
                            <span className="text-zinc-500">{label}:</span>{" "}
                            <span>{d[k]}</span>
                        </div>
                    ) : null
                )}
            </div>
            {d.lip_sync_warning && (
                <div className="text-[10px] text-rose-300 mt-1">
                    {d.lip_sync_warning}
                </div>
            )}
        </div>
    );
}

function ScoresRow({ scores }) {
    const entries = Object.entries(scores || {});
    if (entries.length === 0) return null;
    return (
        <div
            className="mt-2 flex flex-wrap gap-1 text-[10px] font-mono uppercase tracking-wider"
            data-testid="vp-shot-scores"
        >
            {entries.map(([k, v]) => (
                <span
                    key={k}
                    className="px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-300 bg-zinc-900/40"
                    title={k}
                >
                    {k.replace(/_/g, " ")}: <span className="text-zinc-100">{v}</span>
                </span>
            ))}
        </div>
    );
}

function ContinuityBible({ bible }) {
    const entries = Object.entries(bible || {});
    if (entries.length === 0) return null;
    return (
        <section data-testid="vp-continuity-bible">
            <SectionHeader icon={Layers} title="Continuity bible" />
            <div className="space-y-1.5 text-xs">
                {entries.map(([k, v]) => (
                    <div key={k} className="border-l-2 border-violet-500/30 pl-3">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                            {k.replace(/_/g, " ")}
                        </div>
                        <div className="text-zinc-200">{v}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function QualityDashboard({ pack }) {
    const q = pack.pack_quality || {};
    if (!q.by_dimension) return null;
    return (
        <section data-testid="vp-quality">
            <SectionHeader icon={ShieldAlert} title="Quality scores" />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5 text-[11px] font-mono uppercase tracking-wider">
                {Object.entries(q.by_dimension).map(([k, v]) => (
                    <div
                        key={k}
                        className="border border-zinc-800 rounded-sm px-1.5 py-1"
                    >
                        <div className="text-zinc-500 text-[10px]">{k.replace(/_/g, " ")}</div>
                        <div className="text-zinc-100 mt-0.5">{v}</div>
                    </div>
                ))}
                <div className="border border-violet-500/30 rounded-sm px-1.5 py-1 bg-violet-500/[0.05]">
                    <div className="text-zinc-500 text-[10px]">avg</div>
                    <div className="text-violet-200 mt-0.5">{q.average}</div>
                </div>
            </div>
            {q.weakest_dimension && (
                <div className="text-[10px] text-amber-300 mt-2">
                    Weakest dimension: <span className="font-mono uppercase">{q.weakest_dimension}</span>
                </div>
            )}
        </section>
    );
}
